import React from 'react'
import { Modal } from '@/shared/ui'
import OportunidadeModalRight from '../components/OportunidadeModalRight'
import OportunidadeChartPanel from '../components/OportunidadeChartPanel'
import OportunidadeDebugModal from '../components/OportunidadeDebugModal'
import ReportBugModal from '../components/ReportBugModal'
import MobileOpportunitySheet from '../components/MobileOpportunitySheet'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'

export type OportunidadeModalItem = {
  id: string
  symbol: string
  name?: string | null
  logo_url?: string | null
  kind?: 'opportunity' | 'monitor' | 'closed'
  // Preferências iniciais do painel de gráfico (responsabilidade do módulo de Oportunidades)
  chartInitialTimeframe?: string | null
  chartForceSignalsOverlay?: boolean
}

type Ctx = {
  open: boolean
  item: OportunidadeModalItem | null
  openModal: (item: OportunidadeModalItem) => void
  closeModal: () => void
  openDebug: (item: OportunidadeModalItem) => void
  debugOpen: boolean
  openReport: (item: OportunidadeModalItem) => void
  reportOpen: boolean
}

const Ctx = React.createContext<Ctx | undefined>(undefined)

export function useOportunidadeModal() {
  const ctx = React.useContext(Ctx)
  if (!ctx) throw new Error('useOportunidadeModal must be used within OportunidadeModalProvider')
  return ctx
}

export function OportunidadeModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const [item, setItem] = React.useState<OportunidadeModalItem | null>(null)
  const openModal = React.useCallback((it: OportunidadeModalItem) => { setItem(it); setOpen(true) }, [])
  const closeModal = React.useCallback(() => setOpen(false), [])
  // debug modal state
  const [debugOpen, setDebugOpen] = React.useState(false)
  const [debugItem, setDebugItem] = React.useState<OportunidadeModalItem | null>(null)
  const openDebug = React.useCallback((it: OportunidadeModalItem) => { setDebugItem(it); setDebugOpen(true) }, [])

  // report bug modal state
  const [reportOpen, setReportOpen] = React.useState(false)
  const [reportItem, setReportItem] = React.useState<OportunidadeModalItem | null>(null)
  const openReport = React.useCallback((it: OportunidadeModalItem) => { setReportItem(it); setItem(it); setReportOpen(true) }, [])

  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const isLg = useIsLg()

  return (
    <Ctx.Provider value={{ open, item, openModal, closeModal, openDebug, debugOpen, openReport, reportOpen }}>
      {children}
      <div className="hidden lg:block">
        <Modal open={open && isLg} onOpenChange={(v)=> setOpen(v)} className="w-[95vw] max-w-none h-[95vh] max-h-[95vh] overflow-hidden p-0">
          <div className="grid h-full min-h-0 grid-cols-12">
            <div className={"col-span-8 p-4 h-full min-h-0 " + (isMaster ? 'border-r border-border/60' : '')}>
              <OportunidadeChartPanel />
            </div>
            <div className="col-span-4 p-4 h-full min-h-0 overflow-y-auto">
              <OportunidadeModalRight />
            </div>
          </div>
        </Modal>
      </div>
      {/* Mobile/Tablet full-screen bottom sheet */}
      <MobileOpportunitySheet open={open} onClose={() => setOpen(false)} />

      {/* Debug modal (somente dados brutos / JSON) */}
      <OportunidadeDebugModal open={debugOpen} item={debugItem} onOpenChange={setDebugOpen} />

      {/* Report bug modal (todos os roles podem enviar) */}
      <ReportBugModal open={reportOpen} onOpenChange={setReportOpen} />
    </Ctx.Provider>
  )
}

function useIsLg(): boolean {
  const get = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
    return window.matchMedia('(min-width: 1024px)').matches
  }
  const [state, setState] = React.useState<boolean>(get)
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = () => setState(mq.matches)
    try { mq.addEventListener('change', onChange) } catch { mq.addListener(onChange) }
    setState(mq.matches)
    return () => { try { mq.removeEventListener('change', onChange) } catch { mq.removeListener(onChange) } }
  }, [])
  return state
}
